import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { PointCloudLayer, PolygonLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';

import { AppearanceControls } from './components/AppearanceControls';
import { BrandMark } from './components/BrandMark';
import { MobileSheet } from './components/MobileSheet';
import { SolarControl } from './components/SolarControl';
import { StatsPanel, StatsSummary, type LoadProgress } from './components/StatsPanel';
import { TreeInspector } from './components/TreeInspector';
import { TreeTooltip } from './components/TreeTooltip';
import { WelcomeOverlay } from './components/WelcomeOverlay';
import { useIsPhone, useIsTouch } from './hooks/useMediaQuery';
import {
  AUTO_BATCH_MS,
  AUTO_LOAD_ZOOM,
  AUTO_TREE_CAP,
  CITY_LAT,
  CITY_LON,
  DRAW_SHADOW_HULLS,
  MAX_SELECTED_TREES,
  OVERVIEW_ZOOM,
  SHADING_MODEL,
} from './config';
import { EMPTY_BUFFERS, type CanopyBuffers } from './lib/canopy';
import { classifier } from './lib/canopyClient';
import { loadPointCloud, loadPointClouds } from './lib/pointcloud';
import { dateFromDayHour, daylightRange, getSunVector } from './lib/shadow';
import type { SelectedBuilding, SelectedTree, ShadeMode } from './lib/types';
import { useTreeFolioMap, type TreeHit, type ViewportContents } from './map/useTreeFolioMap';
import './styles/app.css';

/**
 * Where the sun starts: today, now.
 *
 * The 2023 site opened on the summer solstice at 1pm, which shows the canopy at
 * its most flattering but describes a day that is almost never the one you are
 * standing in. Opening on the current moment makes the first thing on screen
 * the shade actually being cast outside.
 *
 * Two adjustments. Day-of-year is measured midnight to midnight and rounded, so
 * the 23- and 25-hour DST days do not land a day off. The hour is clamped into
 * the day's daylight window because outside it the sun is below the horizon and
 * casts nothing -- visiting after dark would otherwise open on a blank analysis
 * that looks broken. At night the app opens at sunset, the nearest real light.
 */
function nowDayAndHour(): { day: number; hour: number } {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day =
    Math.round((startOfToday.getTime() - startOfYear.getTime()) / 86_400_000) + 1;

  const hour = now.getHours() + now.getMinutes() / 60;
  const { min, max } = daylightRange(dateFromDayHour(day, 12), CITY_LAT, CITY_LON);

  return { day, hour: Math.min(Math.max(hour, min), max) };
}

const START = nowDayAndHour();

export default function App() {
  const [welcomeOpen, setWelcomeOpen] = useState(true);
  const [dayOfYear, setDayOfYear] = useState(START.day);
  const [hour, setHour] = useState(START.hour);

  // Trees whose canopy is loaded. `pinned` were clicked and survive panning;
  // `auto` follow the viewport at AUTO_LOAD_ZOOM and above.
  const [pinned, setPinned] = useState<SelectedTree[]>([]);
  const [auto, setAuto] = useState<SelectedTree[]>([]);

  // Buildings currently on screen, refreshed as the camera settles. Only used
  // for shadow casting when CLASSIFY_ALL_BUILDINGS is on.
  const [buildings, setBuildings] = useState<SelectedBuilding[]>([]);
  // Buildings the user clicked, held by bin with their footprint so they do not
  // depend on the viewport query having caught them.
  const [focus, setFocus] = useState<Map<string, SelectedBuilding>>(() => new Map());

  // Both layers start off. The map's first frame is then the canopy itself
  // over a plain basemap -- the thing the site is about -- and the analysis and
  // the massing are things a visitor switches on, rather than a full-strength
  // reading of three shade classes over extruded blocks before they have been
  // told what any of it means.
  const [shadeMode, setShadeMode] = useState<ShadeMode>('foliage');
  const [showBuildings, setShowBuildings] = useState(false);
  const [zoom, setZoom] = useState(0);
  const [hover, setHover] = useState<{ hit: TreeHit; x: number; y: number } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const isPhone = useIsPhone();
  const isTouch = useIsTouch();

  // The phone sheet's detent, and the tapped tree it shows above itself. Both
  // are inert on desktop, where the panels are always open and the card follows
  // the cursor instead.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [inspected, setInspected] = useState<TreeHit | null>(null);

  // handleSelectTree is memoized against the map's lifetime, so it reads this
  // rather than closing over the media query.
  const isTouchRef = useRef(isTouch);
  isTouchRef.current = isTouch;

  const noticeTimer = useRef<number | undefined>(undefined);
  const flash = useCallback((message: string) => {
    setNotice(message);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 4200);
  }, []);

  // Picking a tree replaces the viewport set rather than adding to it. The old
  // behavior unioned the two, so clicking a tree changed nothing you could see
  // and there was no way to look at one tree on its own. Emptying the pins
  // falls straight back to the viewport, which is the resting state.
  //
  // `auto` keeps loading underneath a selection. The point clouds are cached
  // for the session either way (lib/pointcloud.ts), so holding them costs no
  // extra fetches and no meaningful memory -- and it means clearing a selection
  // restores the full view instantly instead of refetching into a visible gap.
  const trees = useMemo(() => (pinned.length > 0 ? pinned : auto), [pinned, auto]);

  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;

  const autoRef = useRef(auto);
  autoRef.current = auto;

  const handleSelectTree = useCallback(
    async (hit: TreeHit) => {
      const id = Number(hit.properties.tree_id);
      if (!Number.isFinite(id)) return;

      // On a touchscreen the tap is also the only way to see what this tree is.
      // Raised before any of the early returns below, because the case that
      // most needs explaining -- a tree the LiDAR survey missed, where nothing
      // else happens at all -- is one of them.
      //
      // The card and the expanded sheet are both bottom-anchored detail panels
      // and there is only room for one: with the sheet open the card had
      // nowhere to go but off the top of the screen. Opening one closes the
      // other (see toggleSheet for the other direction).
      if (isTouchRef.current) {
        setInspected(hit);
        setSheetOpen(false);
      }

      // Clicking an already-pinned tree unpins it.
      if (pinnedRef.current.some((t) => t.id === id)) {
        setPinned((prev) => prev.filter((t) => t.id !== id));
        return;
      }

      // The tile already says this tree has nothing to load (see has_canopy in
      // lib/types.ts) -- the hover tooltip explains that. Nothing to do but no
      // reason to interrupt with the flash notice too.
      if (hit.properties.has_canopy === 0) return;

      if (pinnedRef.current.length >= MAX_SELECTED_TREES) {
        flash(`Limit of ${MAX_SELECTED_TREES} pinned trees reached — clear the selection to continue.`);
        return;
      }

      try {
        const pointCount = await loadPointCloud(id);
        if (pointCount === 0) {
          // 17% of the census has nothing: 9.6% stored as `[]`, 7.4% absent.
          // Those render grey, but a stale tile can still let one through.
          flash('No LiDAR canopy was extracted for this tree.');
          return;
        }
        setPinned((prev) =>
          prev.some((t) => t.id === id)
            ? prev
            : [...prev, { id, lon: hit.lon, lat: hit.lat, properties: hit.properties, pointCount }],
        );
      } catch {
        flash('Could not load this tree’s canopy.');
      }
    },
    [flash],
  );

  // Clicking a building raises it and makes it cast a shadow. Clicking again
  // puts it back down.
  const handleSelectBuilding = useCallback((building: SelectedBuilding) => {
    setFocus((prev) => {
      const next = new Map(prev);
      if (next.has(building.bin)) next.delete(building.bin);
      else next.set(building.bin, building);
      return next;
    });
  }, []);

  const handleHoverTree = useCallback(
    (hit: TreeHit | null, point: { x: number; y: number } | null) => {
      setHover(hit && point ? { hit, x: point.x, y: point.y } : null);
    },
    [],
  );

  // One in-flight batch at a time: panning quickly must abandon the previous
  // viewport's fetches rather than queue every intermediate frame's worth.
  const autoAbort = useRef<AbortController | null>(null);
  const flushTimer = useRef<number | undefined>(undefined);

  /**
   * How much of the current viewport's canopy has arrived. Null when nothing is
   * outstanding, so the indicator is absent rather than parked at 100%.
   *
   * Counted in trees, not bytes: the per-tree files run 60-880 KB and the
   * archive does not advertise a length, so a byte total is only knowable after
   * the fact. Trees are also the unit the cap is expressed in, which makes the
   * denominator the same number AUTO_TREE_CAP is documented against.
   */
  const [loadProgress, setLoadProgress] = useState<LoadProgress | null>(null);

  const handleViewportChange = useCallback(
    (view: ViewportContents) => {
      setBuildings(view.buildings);

      autoAbort.current?.abort();
      window.clearTimeout(flushTimer.current);
      flushTimer.current = undefined;

      if (view.zoom < AUTO_LOAD_ZOOM) {
        // Below the threshold a viewport is hundreds of megabytes of LiDAR.
        setAuto((prev) => (prev.length === 0 ? prev : []));
        setLoadProgress(null);
        return;
      }

      // Nearest to the center first, so the cap keeps what you are looking at.
      const [cx, cy] = view.center;
      const wanted = [...view.trees]
        .sort(
          (a, b) =>
            (a.lon - cx) ** 2 + (a.lat - cy) ** 2 - ((b.lon - cx) ** 2 + (b.lat - cy) ** 2),
        )
        .slice(0, AUTO_TREE_CAP);

      const meta = new Map(wanted.map((t) => [Number(t.properties.tree_id), t]));
      const controller = new AbortController();
      autoAbort.current = controller;

      // Drop anything that left the viewport, keep what is still wanted.
      const keep = autoRef.current.filter((t) => meta.has(t.id));
      if (keep.length !== autoRef.current.length) setAuto(keep);

      const already = new Set(keep.map((t) => t.id));

      // Clouds arrive one at a time, but every state update reclassifies the
      // whole selection. Committing per tree would be quadratic -- 250 rebuilds
      // over a growing set. Batching on a short timer keeps the fill visibly
      // progressive while doing the heavy work a handful of times.
      const pending: SelectedTree[] = [];
      const flush = () => {
        flushTimer.current = undefined;
        if (controller.signal.aborted || pending.length === 0) return;
        const batch = pending.splice(0, pending.length);
        setAuto((prev) => {
          const seen = new Set(prev.map((t) => t.id));
          const added = batch.filter((t) => !seen.has(t.id));
          return added.length ? [...prev, ...added] : prev;
        });
      };

      const toLoad = [...meta.keys()].filter((id) => !already.has(id));

      // The bar measures the whole viewport, not just the outstanding fetches.
      // Trees carried over from the last camera are already drawn, so starting
      // the denominator at the fetch count would send the bar back to zero on
      // every small pan and read as though the map had thrown its canopy away.
      setLoadProgress(toLoad.length > 0 ? { loaded: already.size, total: meta.size } : null);

      // Progress lands once per finished fetch -- up to AUTO_TREE_CAP of them --
      // and every one is a state update that re-renders the app. Coalescing on
      // the same rhythm as the scene commits keeps the figure honest without
      // adding a render storm to the load it is describing.
      let settledCount = 0;
      let progressTimer: number | undefined;
      const publishProgress = () => {
        progressTimer = undefined;
        if (controller.signal.aborted) return;
        setLoadProgress({ loaded: already.size + settledCount, total: meta.size });
      };

      void loadPointClouds(
        toLoad,
        (id, pointCount) => {
          if (controller.signal.aborted) return;
          const hit = meta.get(id);
          if (!hit) return;
          pending.push({ id, lon: hit.lon, lat: hit.lat, properties: hit.properties, pointCount });
          if (flushTimer.current === undefined) {
            flushTimer.current = window.setTimeout(flush, AUTO_BATCH_MS);
          }
        },
        controller.signal,
        (settled) => {
          settledCount = settled;
          if (progressTimer === undefined) {
            progressTimer = window.setTimeout(publishProgress, AUTO_BATCH_MS);
          }
        },
      ).then(() => {
        flush();
        // Ordering matters: a pending publish would otherwise fire after this
        // and put the bar back on screen, full, with nothing left to load.
        window.clearTimeout(progressTimer);
        // Aborted means a newer viewport has already published its own
        // progress, and clearing here would wipe it.
        if (!controller.signal.aborted) setLoadProgress(null);
      });
    },
    [],
  );

  const {
    containerRef,
    setDeckLayers,
    setSelectedBins,
    setSelectedTreeIds,
    setBuildingsVisible,
  } = useTreeFolioMap({
    onSelectTree: handleSelectTree,
    onSelectBuilding: handleSelectBuilding,
    onHoverTree: handleHoverTree,
    onZoomChange: setZoom,
    onViewportChange: handleViewportChange,
  });

  // Scrubbing the sun reclassifies every loaded point, so let React keep the
  // control responsive and catch the heavy recompute up behind it.
  const deferredDay = useDeferredValue(dayOfYear);
  const deferredHour = useDeferredValue(hour);

  const sun = useMemo(() => {
    // The 2023 code used whichever tree was clicked last as the observer.
    // Averaging the selection (falling back to the city centroid) is stable and
    // differs by well under a hundredth of a degree across the five boroughs.
    const lat = trees.length ? trees.reduce((s, t) => s + t.lat, 0) / trees.length : CITY_LAT;
    const lon = trees.length ? trees.reduce((s, t) => s + t.lon, 0) / trees.length : CITY_LON;
    return getSunVector(dateFromDayHour(deferredDay, deferredHour), lat, lon);
  }, [deferredDay, deferredHour, trees]);

  // Which buildings cast shadows and classify canopy. Everything in view until
  // the user clicks one, at which point the analysis narrows to their choice.
  //
  // Previously every viewport building always shaded and clicking only changed
  // which ones the tally counted, so isolating a building recolored nothing
  // and the promise in the caption went unmet. Narrowing the cast set is what
  // actually answers "what does this tree do to THAT building".
  const shadingBuildings = useMemo(
    () => (focus.size > 0 ? [...focus.values()] : buildings),
    [buildings, focus],
  );

  // Classification runs in a worker, so this arrives a beat after the inputs
  // change rather than with them. Holding the previous buffers until the next
  // ones land is what keeps a sun scrub showing canopy the whole way instead of
  // blinking empty between steps; `useDeferredValue` above already limits how
  // often a new one is asked for.
  const [buffers, setBuffers] = useState<CanopyBuffers>(EMPTY_BUFFERS);

  useEffect(() => {
    if (trees.length === 0) {
      setBuffers({ ...EMPTY_BUFFERS, hulls: [] });
      return;
    }

    let live = true;
    void classifier
      .classify({
        treeIds: trees.map((t) => t.id),
        buildings: shadingBuildings,
        sun,
        shadeMode,
        focused: focus.size > 0,
        model: SHADING_MODEL,
      })
      // A null result was superseded by a later sun position; there is nothing
      // to draw and the request that replaced it is already on its way.
      .then((next) => {
        if (live && next) setBuffers(next);
      });

    return () => {
      live = false;
    };
  }, [trees, shadingBuildings, sun, shadeMode, focus]);

  useEffect(() => {
    setSelectedBins([...focus.keys()]);
  }, [focus, setSelectedBins]);

  useEffect(() => {
    setSelectedTreeIds(pinned.map((t) => t.id));
  }, [pinned, setSelectedTreeIds]);

  useEffect(() => {
    setBuildingsVisible(showBuildings);
  }, [showBuildings, setBuildingsVisible]);

  useEffect(() => {
    const layers: Layer[] = [];

    if (DRAW_SHADOW_HULLS && buffers.hulls.length > 0) {
      layers.push(
        new PolygonLayer({
          id: 'building-shadows',
          data: buffers.hulls,
          getPolygon: (d: unknown) => d as Array<[number, number]>,
          getFillColor: [30, 36, 52, 120],
          stroked: false,
          filled: true,
          extruded: false,
          pickable: false,
        }),
      );
    }

    if (buffers.length > 0) {
      // Ground shadow first so canopy points draw over it.
      layers.push(
        new PointCloudLayer({
          id: 'canopy-shadow',
          data: {
            length: buffers.length,
            attributes: {
              getPosition: { value: buffers.shadowPositions, size: 3 },
              // Size 4: the alpha channel drops the dots belonging to canopy
              // that is in shade and so casts nothing (see lib/canopy.ts).
              getColor: { value: buffers.shadowColors, size: 4, normalized: true },
            },
          },
          pointSize: 4,
          opacity: 0.18,
          pickable: false,
        }),
        new PointCloudLayer({
          id: 'canopy',
          data: {
            length: buffers.length,
            attributes: {
              getPosition: { value: buffers.canopyPositions, size: 3 },
              getColor: { value: buffers.canopyColors, size: 3, normalized: true },
            },
          },
          pointSize: 3,
          opacity: 0.9,
          pickable: false,
        }),
      );
    }

    setDeckLayers(layers);
  }, [buffers, setDeckLayers]);

  // Clears what the user chose: pinned trees and the building focus. Auto-loaded
  // canopies follow the viewport and are not the user's to clear.
  const reset = useCallback(() => {
    setPinned([]);
    setFocus(new Map());
    setInspected(null);
  }, []);

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  const toggleSheet = useCallback(() => {
    setSheetOpen((v) => !v);
    setInspected(null);
  }, []);

  // Only pinned trees are "selected" for the tooltip. Every tree in view has
  // its canopy loaded, so keying this off `trees` marked all of them.
  const pinnedIds = useMemo(() => new Set(pinned.map((t) => t.id)), [pinned]);

  const stats = (
    <StatsPanel
      stats={buffers.stats}
      zoom={zoom}
      shadeMode={shadeMode}
      loading={loadProgress}
      individualSelection={pinned.length > 0}
      variant={isPhone ? 'flow' : 'inline'}
    />
  );

  const appearance = (
    <AppearanceControls
      shadeMode={shadeMode}
      showBuildings={showBuildings}
      canReset={pinned.length > 0 || focus.size > 0}
      onShadeMode={setShadeMode}
      onShowBuildings={setShowBuildings}
      onReset={reset}
      variant={isPhone ? 'flow' : 'panel'}
    />
  );

  const solar = (
    <SolarControl
      dayOfYear={dayOfYear}
      hour={hour}
      onDayChange={setDayOfYear}
      onHourChange={setHour}
      variant={isPhone ? 'flow' : 'panel'}
      compact={isPhone}
    />
  );

  return (
    <div className="app" data-sheet={isPhone && sheetOpen ? 'expanded' : 'collapsed'}>
      <div className="map-root" ref={containerRef} />
      <div className="grain" />

      <BrandMark />

      <header className="masthead panel enter enter--1">
        <div className="masthead__row">
          <span className="masthead__mark">
            Tree Folio <em>NYC</em>
          </span>
          <button
            type="button"
            className="help-btn"
            onClick={() => setWelcomeOpen(true)}
            aria-label="How to use this map"
            title="How to use this map"
          >
            ?
          </button>
        </div>
        {/* On a phone the stats live in the sheet below instead, next to the
            appearance controls -- see the branch below. */}
        {!isPhone && stats}
      </header>

      {/*
        Same three controls either way -- what changes is whether they float
        over the map independently, or stack inside one sheet the map can be
        seen above. On a phone the floating arrangement left the map as a strip.
      */}
      {isPhone ? (
        <MobileSheet
          expanded={sheetOpen}
          onToggle={toggleSheet}
          summary={
            <StatsSummary
              stats={buffers.stats}
              zoom={zoom}
              shadeMode={shadeMode}
              loading={loadProgress}
            />
          }
          primary={solar}
        >
          {stats}
          {appearance}
        </MobileSheet>
      ) : (
        <>
          {appearance}
          {solar}
        </>
      )}

      {!isTouch && hover && zoom >= OVERVIEW_ZOOM && (
        <TreeTooltip
          properties={hover.hit.properties}
          x={hover.x}
          y={hover.y}
          selected={pinnedIds.has(Number(hover.hit.properties.tree_id))}
        />
      )}

      {isTouch && inspected && (
        <TreeInspector
          properties={inspected.properties}
          selected={pinnedIds.has(Number(inspected.properties.tree_id))}
          onToggleSelect={() => void handleSelectTree(inspected)}
          onClose={() => setInspected(null)}
        />
      )}

      {notice && (
        <div className="notice panel" role="status">
          {notice}
        </div>
      )}

      {welcomeOpen && <WelcomeOverlay onDismiss={() => setWelcomeOpen(false)} />}
    </div>
  );
}
